/* ==========================================================================
   SOLVANE — theme.js
   Vanilla JS. No dependencies. Progressive enhancement over real Shopify
   Ajax Cart API + native form submissions.
   ========================================================================== */
(function () {
  'use strict';

  window.theme = window.theme || {};
  theme.routes = theme.routes || {
    cart_add_url: '/cart/add.js',
    cart_change_url: '/cart/change.js',
    cart_url: '/cart',
    root: '/'
  };
  theme.moneyFormat = theme.moneyFormat || (window.Shopify && Shopify.money_format) || '{{amount}} €';

  /* ---------------- Money formatting ---------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || theme.moneyFormat;

    function defaultOption(opt, def) { return typeof opt === 'undefined' ? def : opt; }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }
  theme.formatMoney = formatMoney;

  /* ---------------- Small helpers ---------------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function trapFocus(container) {
    var focusable = qsa('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
    if (!focusable.length) return;
    focusable[0].focus();
  }

  /* ==========================================================================
     REVEAL ON SCROLL
     ========================================================================== */
  function initReveal() {
    var items = qsa('[data-reveal]');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    items.forEach(function (el, i) {
      el.style.setProperty('--reveal-index', i % 8);
      observer.observe(el);
    });
  }

  /* ==========================================================================
     GENERIC DISCLOSURE (mobile menu, search drawer, cart drawer)
     ========================================================================== */
  function bindOpenClose(root, openTriggers, closeTriggers) {
    if (!root) return;
    function open() {
      root.setAttribute('data-open', 'true');
      document.documentElement.style.overflow = 'hidden';
      trapFocus(root);
    }
    function close() {
      root.setAttribute('data-open', 'false');
      document.documentElement.style.overflow = '';
    }
    qsa(openTriggers).forEach(function (btn) { btn.addEventListener('click', open); });
    qsa(closeTriggers, root).forEach(function (btn) { btn.addEventListener('click', close); });
    root.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    root.open = open;
    root.close = close;
    return root;
  }

  var mobileMenu, searchDrawer, cartDrawerRoot;

  /* ==========================================================================
     CART
     ========================================================================== */
  var CartDrawer = {
    el: null,
    init: function () {
      this.el = qs('#CartDrawer');
      if (!this.el) return;
      cartDrawerRoot = bindOpenClose(this.el, '[data-cart-open]', '[data-cart-close]');
      this.bindLineEvents();
    },
    bindLineEvents: function () {
      var self = this;
      // Delegated so it survives re-renders
      document.body.addEventListener('click', function (e) {
        var minus = e.target.closest('[data-cart-qty-minus]');
        var plus = e.target.closest('[data-cart-qty-plus]');
        var remove = e.target.closest('[data-cart-remove]');
        if (minus || plus) {
          var wrap = (minus || plus).closest('[data-cart-line]');
          var input = qs('input', wrap);
          var line = wrap.getAttribute('data-cart-line');
          var qty = parseInt(input.value, 10) || 1;
          qty = plus ? qty + 1 : Math.max(0, qty - 1);
          self.updateLine(line, qty);
        }
        if (remove) {
          e.preventDefault();
          var lineEl = remove.closest('[data-cart-line]');
          self.updateLine(lineEl.getAttribute('data-cart-line'), 0);
        }
      });
      document.body.addEventListener('change', function (e) {
        if (e.target.matches('[data-cart-line] input[type="number"]')) {
          var wrap = e.target.closest('[data-cart-line]');
          var qty = Math.max(0, parseInt(e.target.value, 10) || 0);
          self.updateLine(wrap.getAttribute('data-cart-line'), qty);
        }
      });
    },
    updateLine: function (line, quantity) {
      this.setLoading(true);
      fetch(theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function () { CartDrawer.render(true); })
        .catch(function () { CartDrawer.setLoading(false); });
    },
    addItem: function (formData, submitter) {
      this.setLoading(true);
      return fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
        .then(function (res) {
          if (!res.ok) {
            CartDrawer.setLoading(false);
            return Promise.reject(res.json);
          }
          return CartDrawer.render(true);
        });
    },
    render: function (open) {
      var url = (theme.routes.cart_url || '/cart') + '?section_id=cart-drawer';
      return fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.getElementById('CartDrawer');
          if (fresh && CartDrawer.el) CartDrawer.el.innerHTML = fresh.innerHTML;
          var countEl = doc.querySelector('[data-cart-item-count]');
          var count = countEl ? countEl.textContent.trim() : '0';
          qsa('[data-cart-count]').forEach(function (el) {
            el.textContent = count;
            el.setAttribute('data-count', count);
          });
          CartDrawer.setLoading(false);
          if (open && cartDrawerRoot) cartDrawerRoot.open();
          var live = qs('#a11y-cart-refresh-message');
          if (live) live.textContent = 'Carrito actualizado';
        });
    },
    setLoading: function (state) {
      if (this.el) this.el.classList.toggle('is-loading', state);
    }
  };
  theme.CartDrawer = CartDrawer;

  function initProductForms() {
    document.body.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-product-form], form.product-form');
      if (!form) return;

      var submitter = e.submitter;
      if (submitter && submitter.hasAttribute('data-buy-now')) {
        // Let the browser submit natively straight to checkout via return_to.
        return;
      }

      e.preventDefault();
      var submitBtn = qs('[data-add-to-cart]', form);
      var errorEl = qs('[data-form-error]', form);
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      if (submitBtn) submitBtn.setAttribute('data-loading', 'true');

      var formData = new FormData(form);
      formData.delete('return_to');

      CartDrawer.addItem(formData)
        .then(function () {
          if (submitBtn) submitBtn.removeAttribute('data-loading');
        })
        .catch(function (jsonPromise) {
          if (submitBtn) submitBtn.removeAttribute('data-loading');
          Promise.resolve(jsonPromise).then(function (json) {
            if (errorEl) {
              errorEl.textContent = (json && json.description) || 'No se pudo añadir el producto.';
              errorEl.hidden = false;
            }
          });
        });
    });
  }

  /* ==========================================================================
     QUANTITY SELECTORS (product form)
     ========================================================================== */
  function initQuantitySelectors() {
    document.body.addEventListener('click', function (e) {
      var minus = e.target.closest('[data-qty-minus]');
      var plus = e.target.closest('[data-qty-plus]');
      if (!minus && !plus) return;
      var wrap = (minus || plus).closest('.qty-selector');
      var input = qs('input', wrap);
      var val = parseInt(input.value, 10) || 1;
      val = plus ? val + 1 : Math.max(1, val - 1);
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ==========================================================================
     VARIANT PICKER
     ========================================================================== */
  function initVariantPickers() {
    qsa('[data-product-form], form.product-form').forEach(function (form) {
      var dataEl = qs('[data-product-json]', form);
      var product = null;
      if (dataEl && dataEl.textContent) {
        try { product = JSON.parse(dataEl.textContent); } catch (err) { product = null; }
      }

      function findVariant(options) {
        if (!product || !product.variants) return null;
        return product.variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === options[i]; });
        });
      }

      function updateUI(variant) {
        var priceScope = form.closest('.product-buybox__primary') || form.closest('.product-buybox') || form.parentElement || form;
        var priceWrap = qs('[data-price-wrap]', priceScope);
        var addBtn = qs('[data-add-to-cart]', form);
        var buyNowBtn = qs('[data-buy-now]', form);
        var idInput = qs('[name="id"]', form);
        var current = qs('[data-price-current]', priceScope);
        var compare = qs('[data-price-compare]', priceScope);
        var stickyPrice = qs('[data-sticky-buy-bar-price]');

        if (!variant) {
          if (addBtn) { addBtn.disabled = true; qs('.btn-label', addBtn).textContent = addBtn.dataset.unavailableLabel || 'No disponible'; }
          if (buyNowBtn) buyNowBtn.disabled = true;
          return;
        }

        if (idInput) idInput.value = variant.id;
        if (current) current.textContent = formatMoney(variant.price);
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
        if (compare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compare.textContent = formatMoney(variant.compare_at_price);
            compare.hidden = false;
          } else {
            compare.hidden = true;
          }
        }

        if (addBtn) {
          var label = qs('.btn-label', addBtn);
          if (!variant.available) {
            addBtn.disabled = true;
            if (label) label.textContent = addBtn.dataset.soldOutLabel || 'Agotado';
          } else {
            addBtn.disabled = false;
            if (label) label.textContent = addBtn.dataset.defaultLabel || 'Añadir al carrito';
          }
        }
        if (buyNowBtn) buyNowBtn.disabled = !variant.available;

        if (variant.featured_media) {
          var img = qs('.product-gallery__thumb[data-media-id="' + variant.featured_media.id + '"]', document);
          if (img) img.click();
        }

        // Reflect selected values in labels
        qsa('[data-variant-picker-value]', form).forEach(function (el) {
          var idx = el.getAttribute('data-variant-picker-value');
          el.textContent = variant.options[idx] || '';
        });
      }

      form.addEventListener('change', function (e) {
        if (!e.target.matches('[name^="options["]')) return;

        // Direct path: use the price Shopify already rendered for this exact
        // variant (embedded as a data attribute on the selected input), so the
        // displayed price never depends on JS-side money formatting or on
        // matching option arrays.
        var directPrice = e.target.getAttribute('data-variant-price');
        var directId = e.target.getAttribute('data-variant-id');
        if (directPrice && directId) {
          var directScope = form.closest('.product-buybox__primary') || form.closest('.product-buybox') || form.parentElement || form;
          var directCurrent = qs('[data-price-current]', directScope);
          var directSticky = qs('[data-sticky-buy-bar-price]');
          var directIdInput = qs('[name="id"]', form);
          if (directCurrent) directCurrent.textContent = directPrice;
          if (directSticky) directSticky.textContent = directPrice;
          if (directIdInput) directIdInput.value = directId;
        }

        if (!product) return;

        var options = qsa('[name^="options["]', form).reduce(function (acc, el) {
          var idx = parseInt(el.getAttribute('data-option-index'), 10);
          if ((el.type === 'radio' && el.checked) || el.tagName === 'SELECT') acc[idx] = el.value;
          return acc;
        }, []);
        var variant = findVariant(options);
        updateUI(variant);
      });
    });
  }

  /* ==========================================================================
     PRODUCT GALLERY
     ========================================================================== */
  function initGalleries() {
    qsa('[data-gallery]').forEach(function (gallery) {
      var slides = qsa('[data-gallery-slide]', gallery);
      var thumbs = qsa('[data-gallery-thumb]', gallery);
      function setActive(index) {
        slides.forEach(function (s, i) { s.setAttribute('data-active', i === index ? 'true' : 'false'); });
        thumbs.forEach(function (t, i) { t.setAttribute('data-active', i === index ? 'true' : 'false'); });
      }
      thumbs.forEach(function (t, i) { t.addEventListener('click', function () { setActive(i); }); });
      var prev = qs('[data-gallery-prev]', gallery);
      var next = qs('[data-gallery-next]', gallery);
      function currentIndex() { return slides.findIndex(function (s) { return s.getAttribute('data-active') === 'true'; }); }
      if (prev) prev.addEventListener('click', function () {
        var i = currentIndex(); setActive((i - 1 + slides.length) % slides.length);
      });
      if (next) next.addEventListener('click', function () {
        var i = currentIndex(); setActive((i + 1) % slides.length);
      });
    });
  }

  /* ==========================================================================
     ACCORDIONS (FAQ + product tabs)
     ========================================================================== */
  function initAccordions() {
    qsa('[data-accordion-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        var group = trigger.closest('[data-accordion-group]');

        if (group && group.hasAttribute('data-accordion-single')) {
          qsa('[data-accordion-trigger]', group).forEach(function (t) {
            if (t !== trigger) {
              t.setAttribute('aria-expanded', 'false');
              var p = document.getElementById(t.getAttribute('aria-controls'));
              if (p) p.style.maxHeight = null;
            }
          });
        }

        trigger.setAttribute('aria-expanded', String(!expanded));
        if (!expanded) {
          panel.style.maxHeight = panel.scrollHeight + 'px';
        } else {
          panel.style.maxHeight = null;
        }
      });
    });
  }

  /* ==========================================================================
     STICKY MOBILE BUY BAR
     ========================================================================== */
  function initStickyBuyBar() {
    var bar = qs('[data-sticky-buy-bar]');
    var anchor = qs('[data-buy-bar-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
      });
    }, { rootMargin: '-64px 0px 0px 0px' });
    observer.observe(anchor);

    var scrollBtn = qs('[data-sticky-buy-scroll]', bar);
    if (scrollBtn) {
      scrollBtn.addEventListener('click', function () {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  /* ==========================================================================
     NEWSLETTER (progressive enhancement over native Shopify customer form)
     ========================================================================== */
  function initNewsletterForms() {
    qsa('[data-newsletter-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var msg = qs('[data-newsletter-msg]', form);
        var input = qs('input[type="email"]', form);
        var formData = new FormData(form);
        fetch(form.action, { method: 'POST', body: formData, headers: { Accept: 'text/html' } })
          .then(function () {
            if (msg) { msg.textContent = form.dataset.successMessage || 'Gracias.'; msg.hidden = false; }
            if (input) input.value = '';
          })
          .catch(function () {
            if (msg) { msg.textContent = form.dataset.errorMessage || 'Inténtalo de nuevo.'; msg.hidden = false; }
          });
      });
    });
  }

  /* ==========================================================================
     INIT
     ========================================================================== */
  document.addEventListener('DOMContentLoaded', function () {
    mobileMenu = bindOpenClose(qs('#MobileMenu'), '[data-menu-open]', '[data-menu-close]');
    searchDrawer = bindOpenClose(qs('#SearchDrawer'), '[data-search-open]', '[data-search-close]');
    CartDrawer.init();

    initReveal();
    initProductForms();
    initQuantitySelectors();
    initVariantPickers();
    initGalleries();
    initAccordions();
    initStickyBuyBar();
    initNewsletterForms();

    if (searchDrawer) {
      qsa('[data-search-open]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          setTimeout(function () { var i = qs('input', searchDrawer); if (i) i.focus(); }, 60);
        });
      });
    }
  });
})();
